import {
  Children,
  cloneElement,
  ComponentPropsWithRef,
  isValidElement,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
} from 'react';

import { ButtonGroupSize } from './ButtonGroup.types';
import { Separator } from './Separator';

import { ButtonGroupIconButton, ButtonGroupStyled } from './ButtonGroup.styles';

export interface ButtonGroupProps extends ComponentPropsWithRef<'div'> {
  children?:
    | ReactElement<{ disabled?: boolean; 'aria-disabled'?: boolean }>[]
    | ReactElement<{ disabled?: boolean; 'aria-disabled'?: boolean }>;
  size?: ButtonGroupSize;
  disabled?: boolean;
  onClick?: (e?: MouseEvent) => unknown;
}

export function ButtonGroup({
  children,
  size = 'small',
  disabled = false,
  onKeyUp,
  onClick,
  ...props
}: ButtonGroupProps) {
  const handleClick = (e?: MouseEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }

    onClick?.(e);
  };

  const handleKeyUp = (e: KeyboardEvent<HTMLDivElement>) => {
    onKeyUp?.(e);

    if (e.target === e.currentTarget && (e.code === 'Enter' || e.code === 'Space')) {
      handleClick();
    }
  };

  return (
    <ButtonGroupStyled
      role="group"
      aria-label="button-group"
      data-size={size}
      aria-disabled={disabled}
      tabIndex={disabled || !onClick ? undefined : 0}
      onKeyUp={handleKeyUp}
      onClick={handleClick}
      {...props}
    >
      {Children.map(children, (child) => {
        if (isValidElement(child)) {
          return cloneElement(child, {
            disabled: child.props.disabled ?? disabled,
            'aria-disabled': child.props.disabled ?? disabled,
          });
        }

        return child;
      })}
    </ButtonGroupStyled>
  );
}

ButtonGroup.Separator = Separator;
ButtonGroup.IconButton = ButtonGroupIconButton;
